import React, {ReactNode} from "react";
import {useModal} from "@ebay/nice-modal-react";
import {Dialog, DialogActions, DialogContent, DialogTitle} from "@mui/material";
import {Box, Colors, Container, IconAlert, IconButton, IconClose, Text} from "@bounce/shared/ui";
import {Button} from "../../../ui/components";

const DIALOG_MAX_WIDTH = 960;
const CANCEL_LABEL = "Cancel";
const SAVE_CHANGES_LABEL = "Save changes";
const SAVE_TEST_LABEL = "Save test";

type Props = {
  title: string;
  isEdit: boolean;
  isBusy: boolean;
  isDirty: boolean;
  issue: string | null;
  onSubmit: () => void;
  children: ReactNode;
};

const FlowDialog = ({title, isEdit, isBusy, isDirty, issue, onSubmit, children}: Props) => {
  const modal = useModal();

  return (
    <Dialog
      open={modal.visible}
      onClose={() => modal.hide()}
      maxWidth={false}
      slotProps={{
        transition: {onExited: () => modal.remove()},
        paper: {sx: {borderRadius: "16px", maxWidth: DIALOG_MAX_WIDTH, width: "100%"}},
      }}
    >
      <DialogTitle sx={{p: "20px 24px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text size={20} variant="interDisplay600" sx={{color: Colors.grey._800}}>
          {title}
        </Text>
        <IconButton
          onClick={() => modal.hide()}
          size="small"
          aria-label="Close"
          sx={{color: Colors.grey._500, "&:hover": {backgroundColor: Colors.tertiary._200}}}
        >
          <IconClose width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{p: "0 24px 24px"}}>
        <Box component="form" onSubmit={onSubmit} sx={{display: "flex", flexDirection: "column", gap: 2}}>
          {children}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{p: "16px 24px", justifyContent: "space-between", borderTop: `1px solid ${Colors.tertiary._200}`}}
      >
        <Container row sx={{gap: 1, alignItems: "center", minHeight: 32}}>
          {issue && (
            <>
              <IconAlert width={18} height={18} color={Colors.red._900} />
              <Text size={13} sx={{color: Colors.red._900}}>
                {issue}
              </Text>
            </>
          )}
        </Container>
        <Container row sx={{gap: 1}}>
          <Button buttonType="outlined-secondary" size="small" onClick={() => modal.hide()} disabled={isBusy}>
            {CANCEL_LABEL}
          </Button>
          <Button
            buttonType="contained-primary"
            size="small"
            onClick={onSubmit}
            disabled={isBusy || (isEdit && !isDirty)}
          >
            {isEdit ? SAVE_CHANGES_LABEL : SAVE_TEST_LABEL}
          </Button>
        </Container>
      </DialogActions>
    </Dialog>
  );
};

export default FlowDialog;
